import { createContext, useContext, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

import { AttachmentsSection } from '@/components/parts/AttachmentsSection';
import { DocumentsSection } from '@/components/parts/DocumentsSection';
import { EditPartModal } from '@/components/parts/EditPartModal';
import { FindAlternatesModal } from '@/components/parts/FindAlternatesModal';
import { ValidateAgainstDistributorModal } from '@/components/parts/ValidateAgainstDistributorModal';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { useAttachments } from '@/lib/attachments';
import { useWhereUsed } from '@/lib/boms';
import { isPartFieldHidden, isSpecialtyRelevant } from '@/lib/field-relevance';
import { cn } from '@/lib/cn';
import { formatPriceCents } from '@/lib/format';
import {
  INVENTORY_FORMS,
  type InventoryForm,
  type InventoryUpdateInput,
  formatInventoryForm,
  useUpdateInventory,
} from '@/lib/inventory';
import { useLocations } from '@/lib/locations';
import { type PartDetail, formatMounting, useDeletePart, usePartCategories, useUpdatePart } from '@/lib/parts';
import {
  type PartSupplier,
  type SupplierCreateInput,
  useAddSupplier,
  useDeleteSupplier,
  useDismissSupplierEol,
  useDistributors,
  useMarkSupplierEol,
  usePromoteSupplier,
  useSuppliers,
} from '@/lib/sourcing';
import { supplierLink } from '@/lib/supplierLink';

/** When true, `Field` renders its row with an em-dash placeholder
 * instead of hiding when the value is null. The drawer toggles this
 * on in "expanded" mode so the user can see every field that exists
 * for a part — populated or not — without having to open the edit
 * modal. Defaults to false (today's auto-hide behavior). */
const ShowEmptyFieldsContext = createContext(false);
// Drives per-row category-aware visibility — `null` means "no category
// resolved yet", which the rubric treats as "show everything" so a
// freshly-loaded part doesn't lose data-entry surface during the
// categories cache hydration.
const CategorySlugContext = createContext<string | null>(null);

function resolveCategorySlug(
  categoryId: number | null,
  categories: readonly { id: number; slug: string }[] | undefined,
): string | null {
  if (categoryId == null || !categories) return null;
  return categories.find((c) => c.id === categoryId)?.slug ?? null;
}

/** Distributor slugs whose adapter we can call from the validate flow.
 * Add a slug here once its adapter ships — keep in sync with
 * `app/services/distributors/registry.py`. */
const SUPPORTED_VALIDATE_SLUGS = new Set([
  'lcsc',
  'digikey',
  'mouser',
  'newark',
  'arrow',
]);

interface DrawerProps {
  orgSlug: string | undefined;
  partId: number | null;
  detail: PartDetail | undefined;
  loading: boolean;
  onClose: () => void;
  onDeleted?: (partId: number) => void;
}

export function PartDetailDrawer({
  orgSlug,
  partId,
  detail,
  loading,
  onClose,
  onDeleted,
}: DrawerProps) {
  const open = partId !== null;
  // Two-stage drawer: starts at the curated half-screen width, expands
  // to a wider panel that exposes the rarely-used fields and JSON blobs
  // without making the default view feel cluttered. The state lives on
  // the drawer (not localStorage) — switching parts resets it, which
  // matches what users expect from "this drawer is currently expanded".
  const [wide, setWide] = useState(false);
  const [editing, setEditing] = useState(false);
  const [validating, setValidating] = useState(false);

  // Reset wide-mode/editing/validating when the user navigates between
  // parts so a brand-new drawer always opens in its default layout.
  useEffect(() => {
    setWide(false);
    setEditing(false);
    setValidating(false);
  }, [partId]);

  // Suppliers are loaded for both the SuppliersSection below AND so
  // the header can decide whether to offer the "Validate from
  // distributor" button. We hoist the query here so both consumers
  // share the cache instead of fetching twice.
  const headerSuppliers = useSuppliers(orgSlug, detail?.id ?? null);
  // Categories cache for the category-aware visibility provider —
  // shared by every Field row's relevance check + the inline category
  // chip. App-wide cache makes this near-free after the first load.
  const categoriesData = usePartCategories(orgSlug).data;
  // Pick the highest-priority supplier whose distributor has a
  // registered adapter we can call. Today that's just LCSC; future
  // adapters expand this list.
  const validateSupplier = headerSuppliers.data
    ?.filter((s) => SUPPORTED_VALIDATE_SLUGS.has(s.distributor_slug))
    .sort((a, b) => a.priority - b.priority)[0];

  return (
    // z-40 so the drawer stacks above modals at z-30 (bulk-refresh
    // results, etc.). The "Open part →" link inside the bulk modal
    // opens the drawer over the modal so the user can fix one part
    // and return to the same results list afterwards.
    <div
      className={cn(
        'fixed inset-0 z-40 transition-opacity',
        open ? 'pointer-events-auto opacity-100' : 'pointer-events-none opacity-0',
      )}
      aria-hidden={!open}
    >
      <button
        type="button"
        className="absolute inset-0 bg-stone-900/30"
        onClick={onClose}
        aria-label="Close detail panel"
      />
      <aside
        className={cn(
          'absolute right-0 top-0 h-full w-full overflow-y-auto bg-white dark:bg-stone-900 shadow-xl',
          'transition-[max-width,transform] duration-200',
          wide ? 'max-w-5xl' : 'max-w-xl',
          open ? 'translate-x-0' : 'translate-x-full',
        )}
      >
        <div className="flex items-center justify-between border-b border-stone-200 dark:border-stone-800 px-6 py-4">
          <h2 className="text-lg font-semibold">
            {detail ? detail.part_number : loading ? 'Loading…' : ''}
          </h2>
          <div className="flex items-center gap-1">
            {detail && validateSupplier && (
              <button
                type="button"
                onClick={() => setValidating(true)}
                className="rounded px-2 py-1 text-xs font-medium text-forge-700 dark:text-forge-400 hover:bg-forge-50 dark:hover:bg-stone-800"
                title={`Pull current data from ${validateSupplier.distributor_name} and pick which fields to update`}
              >
                Validate…
              </button>
            )}
            {detail && (
              <button
                type="button"
                onClick={() => setEditing(true)}
                className="rounded px-2 py-1 text-xs font-medium text-forge-700 dark:text-forge-400 hover:bg-forge-50 dark:hover:bg-stone-800"
              >
                Edit…
              </button>
            )}
            <button
              type="button"
              onClick={() => setWide((w) => !w)}
              className="rounded p-1 text-xs text-stone-500 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-stone-800"
              aria-label={wide ? 'Collapse' : 'Expand'}
              title={wide ? 'Collapse drawer' : 'Expand for full details'}
            >
              {wide ? '⊟ collapse' : '⊞ expand'}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="rounded p-1 text-stone-500 dark:text-stone-400 hover:bg-stone-100 dark:hover:bg-stone-800"
              aria-label="Close"
            >
              ✕
            </button>
          </div>
        </div>

        {loading && <p className="p-6 text-stone-500 dark:text-stone-400">Loading…</p>}
        {detail && orgSlug && (
          <ShowEmptyFieldsContext.Provider value={wide}>
          <CategorySlugContext.Provider value={resolveCategorySlug(detail.category_id, categoriesData)}>
          <div className="space-y-6 px-6 py-4 text-sm">
            <HeroImage orgSlug={orgSlug} part={detail} />
            <Section title="Identity">
              <Field label="Manufacturer" value={detail.manufacturer} />
              <Field label="Mfg PN" value={detail.mfg_pn} mono />
              <Field label="Alt Mfg" value={detail.alt_manufacturer} />
              <Field label="Alt Mfg PN" value={detail.alt_mfg_pn} mono />
              <Field label="Description" value={detail.description} />
            </Section>

            <Section title="Classification">
              <CategoryField orgSlug={orgSlug} categoryId={detail.category_id} />
              <Field label="Subcategory" value={detail.subcategory} />
              <Field label="Value" value={detail.value} />
              <Field label="Tolerance" value={detail.tolerance} />
              <Field label="Dielectric" value={detail.dielectric} partField="dielectric" />
              <Field label="Polarity" value={detail.polarity} partField="polarity" />
              <Field label="Color" value={detail.color} partField="color" />
              <Field
                label="Wavelength"
                value={formatNumber(detail.wavelength_nm, 'nm')}
                partField="wavelength_nm"
              />
              <Field
                label="Luminous intensity"
                value={formatNumber(detail.luminous_intensity_mcd, 'mcd')}
                partField="luminous_intensity_mcd"
              />
              <Field label="Tags" value={detail.tags?.join(', ') ?? null} />
            </Section>

            <Section title="Physical">
              <Field label="Package" value={detail.package} />
              <Field
                label="Mounting"
                value={detail.mounting ? formatMounting(detail.mounting) : null}
              />
              <Field label="Polarized" value={formatBool(detail.polarized)} />
              <Field label="Pin count" value={detail.pin_count} />
              <Field
                label="Pitch"
                value={formatNumber(detail.pitch_mm, 'mm')}
                partField="pitch_mm"
              />
            </Section>

            <Section title="Electrical">
              <Field
                label="Power"
                value={formatNumber(detail.power_w, 'W')}
                partField="power_w"
              />
              <Field label="Voltage" value={formatNumber(detail.voltage_v, 'V')} />
              <Field label="V max" value={formatNumber(detail.voltage_v_max, 'V')} />
              <Field label="Current" value={formatNumber(detail.current_a, 'A')} />
              <Field label="Vf" value={formatNumber(detail.vf, 'V')} partField="vf" />
              <Field label="Vr" value={formatNumber(detail.vr, 'V')} partField="vr" />
              <Field
                label="Vc clamp"
                value={formatNumber(detail.voltage_clamp_v, 'V')}
                partField="voltage_clamp_v"
              />
              <Field label="Frequency" value={formatNumber(detail.frequency_hz, 'Hz')} />
              <Field
                label="Load capacitance"
                value={formatNumber(detail.load_capacitance_pf, 'pF')}
                partField="load_capacitance_pf"
              />
              <Field
                label="RDS(on)"
                value={formatNumber(detail.rds_on_ohms, 'Ω')}
                partField="rds_on_ohms"
              />
              <Field label="hFE" value={detail.hfe} partField="hfe" />
              <Field
                label="Temp range"
                value={formatTempRange(detail.temp_min_c, detail.temp_max_c)}
              />
            </Section>

            {(isSpecialtyRelevant(
                'regulator_output',
                resolveCategorySlug(detail.category_id, categoriesData),
              )
              || detail.output_voltage_v != null
              || detail.output_voltage_v_max != null
              || detail.output_current_a != null
              || detail.regulator_output_type) && (
              <Section title="Regulator output">
                <Field
                  label="Output voltage"
                  value={formatNumber(detail.output_voltage_v, 'V')}
                />
                <Field
                  label="Output voltage max"
                  value={formatNumber(detail.output_voltage_v_max, 'V')}
                />
                <Field
                  label="Output current"
                  value={formatNumber(detail.output_current_a, 'A')}
                />
                <Field label="Type" value={detail.regulator_output_type} />
              </Section>
            )}

            {(isSpecialtyRelevant(
                'fuse_currents',
                resolveCategorySlug(detail.category_id, categoriesData),
              )
              || detail.hold_current_a != null
              || detail.trip_current_a != null) && (
              <Section title="Fuse currents">
                <Field
                  label="Hold current"
                  value={formatNumber(detail.hold_current_a, 'A')}
                />
                <Field
                  label="Trip current"
                  value={formatNumber(detail.trip_current_a, 'A')}
                />
              </Section>
            )}

            <AttachmentsSection orgSlug={orgSlug} partId={detail.id} />
            <DocumentsSection orgSlug={orgSlug} partId={detail.id} />
            <InventorySection orgSlug={orgSlug} part={detail} />
            <SuppliersSection orgSlug={orgSlug} part={detail} />
            <WhereUsedSection orgSlug={orgSlug} partId={detail.id} />

            <Section title="Lifecycle & datasheet">
              <Field label="Lifecycle" value={detail.lifecycle} />
              <Field label="RoHS" value={formatBool(detail.rohs_compliant)} />
              <Field label="Datasheet" value={detail.datasheet_url} link />
            </Section>

            <Section title="KiCad">
              <Field label="Status" value={kicadStatus(detail)} />
              <Field label="Symbol" value={detail.kicad_symbol} mono />
              <Field label="Footprint" value={detail.kicad_footprint} mono />
              <Field
                label="3D model"
                value={detail.kicad_has_3d_model ? 'Yes' : null}
              />
            </Section>

            {detail.notes && (
              <Section title="Notes">
                <p className="whitespace-pre-wrap text-stone-700 dark:text-stone-300">{detail.notes}</p>
              </Section>
            )}

            <JsonBlobSection title="Custom fields" data={detail.extra_fields} />
            <JsonBlobSection title="External refs" data={detail.external_refs} />
            <JsonBlobSection title="KiCad fields" data={detail.kicad_fields} />

            <Section title="More details">
              <Field
                label="Last updated"
                value={new Date(detail.updated_at).toLocaleString()}
              />
            </Section>

            <DeletePartSection
              orgSlug={orgSlug}
              part={detail}
              onDeleted={() => {
                onDeleted?.(detail.id);
                onClose();
              }}
            />
          </div>
          </CategorySlugContext.Provider>
          </ShowEmptyFieldsContext.Provider>
        )}
      </aside>

      {editing && detail && orgSlug && (
        <EditPartModal
          orgSlug={orgSlug}
          part={detail}
          onClose={() => setEditing(false)}
          onSaved={() => setEditing(false)}
        />
      )}
      {validating && detail && orgSlug && validateSupplier && (
        <ValidateAgainstDistributorModal
          orgSlug={orgSlug}
          part={detail}
          supplier={validateSupplier}
          onClose={() => setValidating(false)}
          onApplied={() => setValidating(false)}
        />
      )}
    </div>
  );
}

function DeletePartSection({
  orgSlug,
  part,
  onDeleted,
}: {
  orgSlug: string;
  part: PartDetail;
  onDeleted: () => void;
}) {
  const del = useDeletePart(orgSlug);

  return (
    <section className="rounded-md border border-red-200 bg-red-50/50 p-3">
      <h3 className="text-xs font-semibold uppercase tracking-wide text-red-700">
        Danger zone
      </h3>
      <p className="mt-1 text-xs text-stone-600 dark:text-stone-400">
        Soft delete: <span className="font-mono">{part.part_number}</span> disappears
        from the parts list and BOM availability checks but the row stays in the
        database. Bring it back via the API if needed.
      </p>
      {del.isError && (
        <p className="mt-2 text-sm text-red-600">{(del.error as Error).message}</p>
      )}
      <Button
        variant="danger"
        className="mt-2"
        disabled={del.isPending}
        onClick={async () => {
          if (
            !confirm(
              `Delete ${part.part_number}? It will disappear from the parts list and BOMs.`,
            )
          ) {
            return;
          }
          try {
            await del.mutateAsync(part.id);
            onDeleted();
          } catch {
            /* surfaced above */
          }
        }}
      >
        {del.isPending ? 'Deleting…' : 'Delete part'}
      </Button>
    </section>
  );
}

function InventorySection({ orgSlug, part }: { orgSlug: string; part: PartDetail }) {
  const inv = part.inventory;
  const locations = useLocations(orgSlug);
  const update = useUpdateInventory(orgSlug, part.id);

  const [editing, setEditing] = useState(false);
  const [qty, setQty] = useState<string>('');
  const [threshold, setThreshold] = useState<string>('');
  const [locationId, setLocationId] = useState<string>('');
  const [form, setForm] = useState<string>('');

  useEffect(() => {
    if (editing) {
      setQty(String(inv?.quantity_on_hand ?? 0));
      setThreshold(String(inv?.quantity_threshold ?? 0));
      setLocationId(inv?.location_id !== null && inv?.location_id !== undefined
        ? String(inv.location_id)
        : '');
      setForm(inv?.form ?? '');
    }
  }, [
    editing,
    inv?.quantity_on_hand,
    inv?.quantity_threshold,
    inv?.location_id,
    inv?.form,
  ]);

  const onSave = async () => {
    const patch: InventoryUpdateInput = {};
    const qNum = Number(qty);
    if (!Number.isNaN(qNum) && qNum !== (inv?.quantity_on_hand ?? 0)) {
      patch.quantity_on_hand = qNum;
    }
    const tNum = Number(threshold);
    if (!Number.isNaN(tNum) && tNum !== (inv?.quantity_threshold ?? 0)) {
      patch.quantity_threshold = tNum;
    }
    const newLoc = locationId === '' ? null : Number(locationId);
    if (newLoc !== (inv?.location_id ?? null)) patch.location_id = newLoc;

    const newForm: InventoryForm | null =
      form === '' ? null : (form as InventoryForm);
    if (newForm !== (inv?.form ?? null)) patch.form = newForm;

    if (Object.keys(patch).length === 0) {
      setEditing(false);
      return;
    }
    try {
      await update.mutateAsync(patch);
      setEditing(false);
    } catch {
      /* surfaced below */
    }
  };

  const locationName =
    inv?.location_id !== null && inv?.location_id !== undefined
      ? (locations.data?.find((l) => l.id === inv.location_id)?.name ?? '—')
      : '—';

  return (
    <Section
      title="Inventory"
      action={
        editing ? (
          <div className="flex items-center gap-2">
            <Button variant="ghost" onClick={() => setEditing(false)}>
              Cancel
            </Button>
            <Button onClick={onSave} disabled={update.isPending}>
              {update.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        ) : (
          <Button variant="secondary" onClick={() => setEditing(true)}>
            Edit
          </Button>
        )
      }
    >
      {!editing && (
        <>
          <Field
            label="On hand"
            value={
              inv
                ? `${inv.quantity_on_hand}${inv.quantity_is_approximate ? '+' : ''}`
                : '0'
            }
          />
          {/* Threshold is only useful for low-stock alerts; hide it
              from the default view to reduce noise and only surface
              it when the part is actually below threshold (a gap
              signal worth showing). The user can always see + edit
              it via the Edit button. */}
          {inv && inv.quantity_threshold > 0
            && inv.quantity_on_hand <= inv.quantity_threshold && (
            <Field
              label="Threshold"
              value={`${inv.quantity_threshold} (low stock)`}
            />
          )}
          <Field label="Location" value={locationName} />
          {inv?.form && (
            <Field label="Form" value={formatInventoryForm(inv.form)} />
          )}
        </>
      )}
      {editing && (
        <div className="space-y-2">
          <LabeledInline label="On hand">
            <Input
              type="number"
              min={0}
              value={qty}
              onChange={(e) => setQty(e.target.value)}
              className="w-32"
            />
          </LabeledInline>
          <LabeledInline label="Threshold">
            <Input
              type="number"
              min={0}
              value={threshold}
              onChange={(e) => setThreshold(e.target.value)}
              className="w-32"
            />
          </LabeledInline>
          <LabeledInline label="Location">
            <select
              className="block w-full max-w-xs rounded-md border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 px-2 py-1.5 text-sm"
              value={locationId}
              onChange={(e) => setLocationId(e.target.value)}
            >
              <option value="">— none —</option>
              {locations.data?.map((loc) => (
                <option key={loc.id} value={String(loc.id)}>
                  {loc.name}
                </option>
              ))}
            </select>
          </LabeledInline>
          <LabeledInline label="Form">
            <select
              className="block w-full max-w-xs rounded-md border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 px-2 py-1.5 text-sm"
              value={form}
              onChange={(e) => setForm(e.target.value)}
            >
              <option value="">— unspecified —</option>
              {INVENTORY_FORMS.map((f) => (
                <option key={f} value={f}>
                  {formatInventoryForm(f)}
                </option>
              ))}
            </select>
          </LabeledInline>
          {update.isError && (
            <p className="text-sm text-red-600">{(update.error as Error).message}</p>
          )}
        </div>
      )}
    </Section>
  );
}

function SuppliersSection({ orgSlug, part }: { orgSlug: string; part: PartDetail }) {
  const suppliers = useSuppliers(orgSlug, part.id);
  const distributors = useDistributors();
  const add = useAddSupplier(orgSlug, part.id);
  const remove = useDeleteSupplier(orgSlug, part.id);
  const promote = usePromoteSupplier(orgSlug, part.id);
  const dismissEol = useDismissSupplierEol(orgSlug, part.id);
  const markEol = useMarkSupplierEol(orgSlug, part.id);
  const updatePart = useUpdatePart(orgSlug, part.id);

  const [adding, setAdding] = useState(false);
  const [distributorId, setDistributorId] = useState<string>('');
  const [pn, setPn] = useState<string>('');
  const [url, setUrl] = useState<string>('');
  const [findingAlternates, setFindingAlternates] = useState(false);

  // The find-alternates fanout searches by MPN; without one there's
  // nothing to fan out. Disabled with tooltip when mfg_pn is empty.
  const mpn = part.mfg_pn?.trim() ?? '';
  const canFindAlternates = mpn.length > 0;

  // Primary-dead detection: when the priority-1 supplier is_eol,
  // surface a callout above the supplier list with actionable CTAs.
  // Aaron hit this with AR10001 — DigiKey discontinued the original
  // SKU and PartForge had no in-app indicator that the source was dead.
  const sortedSuppliers =
    suppliers.data?.slice().sort((a, b) => a.priority - b.priority) ?? [];
  const primary = sortedSuppliers[0];
  const primaryDead = Boolean(primary && primary.is_eol);
  const liveAlternates = sortedSuppliers
    .slice(1)
    .filter((s) => !s.is_eol);

  const reset = () => {
    setDistributorId('');
    setPn('');
    setUrl('');
  };

  const onSave = async () => {
    if (!distributorId || !pn.trim()) return;
    // No priority in the payload — the server appends new suppliers
    // to the bottom of the list. The user promotes via the row's
    // "Set as primary" link if they want to reorder.
    const payload: SupplierCreateInput = {
      distributor_id: Number(distributorId),
      distributor_pn: pn.trim(),
    };
    if (url.trim()) payload.supplier_url = url.trim();
    try {
      await add.mutateAsync(payload);
      reset();
      setAdding(false);
    } catch {
      /* surfaced below */
    }
  };

  // Match the server-side cap (MAX_SUPPLIERS_PER_PART = 10). Past this
  // the user must remove one before adding another. We grey the button
  // out client-side so they don't fill in the form just to get a 409.
  const supplierCount = suppliers.data?.length ?? 0;
  const atCap = supplierCount >= 10;

  return (
    <Section
      title={`Suppliers (${supplierCount}/10)`}
      action={
        adding ? null : (
          <div className="flex items-center gap-2">
            <Button
              variant="secondary"
              onClick={() => setFindingAlternates(true)}
              disabled={!canFindAlternates || atCap}
              title={
                !canFindAlternates
                  ? 'Set the manufacturer part number first to search alternates'
                  : atCap
                    ? 'Up to 10 suppliers per part — remove one first'
                    : 'Search every enabled distributor for this MPN'
              }
            >
              Find alternates
            </Button>
            <Button
              variant="secondary"
              onClick={() => setAdding(true)}
              disabled={atCap}
              title={atCap ? 'Up to 10 suppliers per part — remove one first' : undefined}
            >
              Add supplier
            </Button>
          </div>
        )
      }
    >
      {suppliers.data && suppliers.data.length === 0 && !adding && (
        <p className="text-stone-500 dark:text-stone-400">No suppliers yet.</p>
      )}

      {part.lifecycle === 'obsolete' ? (
        <PartObsoleteCallout
          onUndo={() =>
            updatePart.mutate({
              row_version: part.row_version,
              lifecycle: 'active',
            })
          }
          undoing={updatePart.isPending}
        />
      ) : (
        primaryDead && (
          <PrimaryDeadCallout
            primary={primary!}
            liveAlternates={liveAlternates}
            canFindAlternates={canFindAlternates}
            onFindReplacement={() => setFindingAlternates(true)}
            onPromoteAlternate={(supplierId) => promote.mutate(supplierId)}
            promoting={promote.isPending}
            onMarkPartEol={() => {
              if (
                window.confirm(
                  'Mark this part as EOL? Bulk refresh will skip it. ' +
                    'You can undo from the same callout.',
                )
              ) {
                updatePart.mutate({
                  row_version: part.row_version,
                  lifecycle: 'obsolete',
                });
              }
            }}
            markingPartEol={updatePart.isPending}
          />
        )
      )}

      {suppliers.data?.map((s) => (
        <SupplierRow
          key={s.id}
          supplier={s}
          isPrimary={s.priority === 1}
          onDelete={() => remove.mutate(s.id)}
          deleting={remove.isPending && remove.variables === s.id}
          onPromote={() => promote.mutate(s.id)}
          promoting={promote.isPending && promote.variables === s.id}
          onDismissEol={() => dismissEol.mutate(s.id)}
          dismissingEol={dismissEol.isPending && dismissEol.variables === s.id}
          onMarkEol={() => markEol.mutate(s.id)}
          markingEol={markEol.isPending && markEol.variables === s.id}
        />
      ))}
      {promote.isError && (
        <p className="text-sm text-red-600">{(promote.error as Error).message}</p>
      )}

      {adding && distributors.data && (
        <div className="mt-2 space-y-2 rounded-md border border-stone-200 dark:border-stone-800 bg-stone-50 dark:bg-stone-900 p-3">
          <LabeledInline label="Distributor">
            <select
              className="block w-full rounded-md border border-stone-300 dark:border-stone-700 bg-white dark:bg-stone-900 px-2 py-1.5 text-sm"
              value={distributorId}
              onChange={(e) => setDistributorId(e.target.value)}
            >
              <option value="">— select —</option>
              {distributors.data.map((d) => (
                <option key={d.id} value={String(d.id)}>
                  {d.name}
                </option>
              ))}
            </select>
          </LabeledInline>
          <LabeledInline label="Distributor PN">
            <Input value={pn} onChange={(e) => setPn(e.target.value)} className="w-full" />
          </LabeledInline>
          <LabeledInline label="URL">
            <Input
              type="url"
              placeholder="https://..."
              value={url}
              onChange={(e) => setUrl(e.target.value)}
              className="w-full"
            />
          </LabeledInline>
          {add.isError && (
            <p className="text-sm text-red-600">{(add.error as Error).message}</p>
          )}
          <div className="flex justify-end gap-2 pt-1">
            <Button
              variant="ghost"
              onClick={() => {
                reset();
                setAdding(false);
              }}
            >
              Cancel
            </Button>
            <Button onClick={onSave} disabled={add.isPending || !distributorId || !pn.trim()}>
              {add.isPending ? 'Saving…' : 'Save'}
            </Button>
          </div>
        </div>
      )}

      {findingAlternates && canFindAlternates && (
        <FindAlternatesModal
          orgSlug={orgSlug}
          partId={part.id}
          mpn={mpn}
          manufacturer={part.manufacturer ?? null}
          description={part.description ?? null}
          onClose={() => setFindingAlternates(false)}
        />
      )}
    </Section>
  );
}

function SupplierRow({
  supplier,
  isPrimary,
  onDelete,
  deleting,
  onPromote,
  promoting,
  onDismissEol,
  dismissingEol,
  onMarkEol,
  markingEol,
}: {
  supplier: PartSupplier;
  isPrimary: boolean;
  onDelete: () => void;
  deleting: boolean;
  onPromote: () => void;
  promoting: boolean;
  onDismissEol: () => void;
  dismissingEol: boolean;
  onMarkEol: () => void;
  markingEol: boolean;
}) {
  const url = supplierLink(supplier);
  const eolTooltip = supplier.is_eol
    ? `${eolReasonLabel(supplier.eol_reason)} · detected ${formatDateOrNow(supplier.eol_detected_at)}`
    : undefined;
  const tiers = parsePriceBreaks(supplier.price_breaks);
  const [tiersOpen, setTiersOpen] = useState(false);
  // Compact horizontal row: pill(s) · name · linked PN · price/MOQ.
  // Each fragment hides if its source value is missing so a barebones
  // supplier row stays one short line. Aaron's earlier feedback:
  // vertical layout wasted whitespace; the dot-separated horizontal
  // form puts everything you need to scan a row on a single line.
  // Quantity-tier ladder hangs below the row when expanded — only
  // shown when the adapter actually surfaced more than one break.
  return (
    <div className="flex flex-col gap-1 border-b border-stone-100 dark:border-stone-800 py-2 last:border-0">
      <div className="flex items-center justify-between gap-3">
      <div className="flex flex-wrap items-center gap-x-2 gap-y-1 min-w-0">
        {isPrimary ? (
          <span className="rounded bg-forge-100 dark:bg-forge-900/40 px-1.5 py-0.5 text-xs font-medium text-forge-700 dark:text-forge-400">
            Primary
          </span>
        ) : (
          <span className="rounded bg-stone-100 dark:bg-stone-800 px-1.5 py-0.5 text-xs font-medium text-stone-500 dark:text-stone-400">
            Alternate
          </span>
        )}
        {supplier.is_eol && (
          <span
            className="rounded bg-red-50 dark:bg-red-900/30 px-1.5 py-0.5 text-xs font-medium text-red-700 dark:text-red-400"
            title={eolTooltip}
          >
            EOL
          </span>
        )}
        {!supplier.is_eol && supplier.eol_dismissed_at && (
          <span
            className="rounded bg-stone-100 dark:bg-stone-800 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wide text-stone-500 dark:text-stone-400"
            title={`Was flagged ${eolReasonLabel(supplier.eol_reason)}; you kept this supplier.`}
          >
            kept (was EOL)
          </span>
        )}
        <span className="font-medium">{supplier.distributor_name}</span>
        <span className="font-mono text-stone-700 dark:text-stone-300 truncate">
          {url ? (
            <a
              href={url}
              target="_blank"
              rel="noreferrer"
              className="text-forge-700 dark:text-forge-400 hover:underline"
            >
              {supplier.distributor_pn}
            </a>
          ) : (
            supplier.distributor_pn
          )}
        </span>
        {supplier.price_unit_cents !== null && (
          <span className="text-stone-500 dark:text-stone-400">
            · {formatPriceCents(supplier.price_unit_cents, supplier.currency)}/unit
            {supplier.moq && ` · MOQ ${supplier.moq}`}
            {supplier.lead_time_days !== null && ` · ${supplier.lead_time_days}d lead`}
          </span>
        )}
        {tiers.length > 1 && (
          <button
            type="button"
            onClick={() => setTiersOpen((v) => !v)}
            className="text-xs text-forge-700 dark:text-forge-400 hover:underline"
            aria-expanded={tiersOpen}
          >
            {tiersOpen ? '▾ Hide tiers' : `▸ ${tiers.length} tiers`}
          </button>
        )}
      </div>
      <div className="flex flex-col items-end gap-1">
        {!isPrimary && (
          <button
            type="button"
            onClick={onPromote}
            disabled={promoting}
            className="text-xs text-forge-700 dark:text-forge-400 hover:underline disabled:opacity-50"
          >
            {promoting ? 'Promoting…' : 'Set as primary'}
          </button>
        )}
        {supplier.is_eol ? (
          <button
            type="button"
            onClick={onDismissEol}
            disabled={dismissingEol}
            className="text-xs text-stone-500 dark:text-stone-400 hover:text-stone-700 dark:hover:text-stone-200 hover:underline disabled:opacity-50"
            title="Keep this supplier despite being dead — for tracking residual stock. Auto re-flag is suppressed after dismiss."
          >
            {dismissingEol ? 'Dismissing…' : 'Dismiss EOL'}
          </button>
        ) : (
          <button
            type="button"
            onClick={onMarkEol}
            disabled={markingEol}
            className="text-xs text-stone-400 dark:text-stone-500 hover:text-red-600 hover:underline disabled:opacity-50"
            title="Mark this supplier as dead manually — for cases you noticed before the next bulk-refresh."
          >
            {markingEol ? 'Marking…' : 'Mark EOL'}
          </button>
        )}
        <button
          type="button"
          onClick={onDelete}
          disabled={deleting}
          className="text-xs text-stone-400 dark:text-stone-500 hover:text-red-600 disabled:opacity-50"
        >
          {deleting ? 'Removing…' : 'Remove'}
        </button>
      </div>
      </div>
      {tiersOpen && tiers.length > 0 && (
        <div className="ml-2 mt-1 inline-block rounded-md border border-stone-200 dark:border-stone-700 bg-stone-50 dark:bg-stone-900/60 px-2 py-1.5 text-xs">
          <table className="text-left">
            <thead>
              <tr className="text-stone-500 dark:text-stone-400">
                <th className="pr-4 font-normal">Qty</th>
                <th className="font-normal">Unit price</th>
              </tr>
            </thead>
            <tbody>
              {tiers.map((t) => (
                <tr key={t.qty}>
                  <td className="pr-4 font-mono text-stone-700 dark:text-stone-300">
                    {t.qty.toLocaleString()}+
                  </td>
                  <td className="font-mono text-stone-700 dark:text-stone-300">
                    {formatPriceCents(t.priceUnitCents, t.currency)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


/** Pull the tier ladder out of `PartSupplier.price_breaks` (JSON-typed
 * on the wire). Filters out malformed entries and sorts ascending by
 * quantity threshold. Returns an empty list on missing/null input. */
function parsePriceBreaks(
  raw: Array<Record<string, unknown>> | null | undefined,
): Array<{ qty: number; priceUnitCents: number; currency: string | null }> {
  if (!raw) return [];
  const out: Array<{
    qty: number;
    priceUnitCents: number;
    currency: string | null;
  }> = [];
  for (const entry of raw) {
    if (!entry || typeof entry !== 'object') continue;
    const qty = (entry as { qty?: unknown }).qty;
    const price = (entry as { price_unit_cents?: unknown }).price_unit_cents;
    const currency = (entry as { currency?: unknown }).currency;
    if (typeof qty !== 'number' || typeof price !== 'number') continue;
    out.push({
      qty,
      priceUnitCents: price,
      currency: typeof currency === 'string' ? currency : null,
    });
  }
  out.sort((a, b) => a.qty - b.qty);
  return out;
}


/** Map the server's `eol_reason` enum value to a user-friendly
 * phrase for tooltips. Unknown reasons fall through to the raw
 * string so we don't lose information when the server adds a new
 * reason without a corresponding frontend update. */
function eolReasonLabel(reason: string | null): string {
  switch (reason) {
    case 'not_found_at_distributor':
      return 'Distributor no longer carries this PN';
    case 'lifecycle_obsolete':
      return 'Distributor flagged as obsolete';
    case 'superseded_by_substitute':
      return 'Replaced by a substitute';
    case 'manual':
      return 'Marked manually';
    case null:
      return 'Reason unknown';
    default:
      return reason;
  }
}


function formatDateOrNow(iso: string | null): string {
  if (!iso) return 'recently';
  // Strip subsecond precision so "Z" parses uniformly. SQLite drops
  // timezone on round-trip for some columns; the front-end format is
  // user-facing rather than analytical, so a relative-ish formatting
  // is enough.
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return date.toISOString().slice(0, 10);
}


/** Rendered above the supplier list when the priority-1 supplier
 * is_eol. The user came to find-alternates because something broke;
 * surface the actionable next steps inline so they don't have to
 * hunt for them. */
function PrimaryDeadCallout({
  primary,
  liveAlternates,
  canFindAlternates,
  onFindReplacement,
  onPromoteAlternate,
  promoting,
  onMarkPartEol,
  markingPartEol,
}: {
  primary: PartSupplier;
  liveAlternates: PartSupplier[];
  canFindAlternates: boolean;
  onFindReplacement: () => void;
  onPromoteAlternate: (supplierId: number) => void;
  promoting: boolean;
  onMarkPartEol: () => void;
  markingPartEol: boolean;
}) {
  const [showPicker, setShowPicker] = useState(false);
  return (
    <div className="mb-3 rounded-md border border-red-200 dark:border-red-900/60 bg-red-50/50 dark:bg-red-900/20 px-3 py-2.5 text-sm">
      <div className="flex items-start gap-2">
        <span aria-hidden="true">⚠️</span>
        <div className="min-w-0 flex-1">
          <p className="font-medium text-red-800 dark:text-red-200">
            Primary supplier is dead
          </p>
          <p className="mt-0.5 text-xs text-red-700/80 dark:text-red-300/80">
            {eolReasonLabel(primary.eol_reason)} ({primary.distributor_name}{' '}
            <span className="font-mono">{primary.distributor_pn}</span>).
            {liveAlternates.length === 0
              ? ' No live alternates configured.'
              : ` ${liveAlternates.length} live alternate${liveAlternates.length === 1 ? '' : 's'} available.`}
          </p>
          <div className="mt-1.5 flex flex-wrap items-center gap-2">
            <Button
              variant="secondary"
              onClick={onFindReplacement}
              disabled={!canFindAlternates}
              title={
                canFindAlternates
                  ? 'Search every enabled distributor for this MPN'
                  : 'Set the manufacturer part number first'
              }
            >
              Find a replacement
            </Button>
            {liveAlternates.length > 0 && !showPicker && (
              <Button variant="ghost" onClick={() => setShowPicker(true)}>
                Promote alternate →
              </Button>
            )}
            <Button
              variant="ghost"
              onClick={onMarkPartEol}
              disabled={markingPartEol}
              title="Mark this whole part EOL — bulk refresh will skip it"
            >
              Mark part EOL
            </Button>
          </div>
          {showPicker && liveAlternates.length > 0 && (
            <div className="mt-2 space-y-1 rounded border border-stone-200 dark:border-stone-700 bg-white dark:bg-stone-900 p-2">
              <p className="text-xs text-stone-500 dark:text-stone-400">
                Pick the alternate to make primary:
              </p>
              {liveAlternates.map((alt) => (
                <button
                  key={alt.id}
                  type="button"
                  onClick={() => onPromoteAlternate(alt.id)}
                  disabled={promoting}
                  className="block w-full text-left rounded px-2 py-1 text-xs hover:bg-stone-100 dark:hover:bg-stone-800 disabled:opacity-50"
                >
                  <span className="font-medium">{alt.distributor_name}</span>{' '}
                  <span className="font-mono text-stone-600 dark:text-stone-400">
                    {alt.distributor_pn}
                  </span>
                </button>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

/** Rendered above the supplier list when `Part.lifecycle === 'obsolete'`.
 * Replaces the dead-primary callout — once the user has declared the
 * whole part dead, hunting for replacement suppliers is moot. Bulk
 * refresh skips obsolete parts; this callout reminds the user of that
 * and offers a one-click undo. */
function PartObsoleteCallout({
  onUndo,
  undoing,
}: {
  onUndo: () => void;
  undoing: boolean;
}) {
  return (
    <div className="mb-3 rounded-md border border-stone-300 dark:border-stone-700 bg-stone-100/70 dark:bg-stone-800/40 px-3 py-2.5 text-sm">
      <div className="flex items-start gap-2">
        <span aria-hidden="true">🪦</span>
        <div className="min-w-0 flex-1">
          <p className="font-medium text-stone-800 dark:text-stone-200">
            Part is marked EOL
          </p>
          <p className="mt-0.5 text-xs text-stone-600 dark:text-stone-400">
            Bulk refresh will skip this part. Existing inventory and BOM
            references stay intact for residual-stock tracking.
          </p>
          <div className="mt-1.5">
            <Button variant="ghost" onClick={onUndo} disabled={undoing}>
              Undo (back to active)
            </Button>
          </div>
        </div>
      </div>
    </div>
  );
}

/** Top-of-drawer product image. Prefers the first locally-attached
 * image (downloaded by the lookup endpoint or spreadsheet importer);
 * falls back to a direct render of `Part.image_url` when no
 * attachment is in place yet — `referrerPolicy="no-referrer"` keeps
 * non-LCSC CDNs happy. LCSC's CDN hot-link blocks any external
 * referrer, so the local-attachment path is the only reliable render
 * for LCSC images; the direct fallback will broken-icon for them
 * until the async download finishes.
 *
 * Renders nothing when neither source is set — keeps the drawer
 * tidy for parts that don't have an image at all. */
function HeroImage({
  orgSlug,
  part,
}: {
  orgSlug: string;
  part: PartDetail;
}) {
  const attachments = useAttachments(orgSlug, part.id, 'image');
  const first = attachments.data?.[0];
  const src = first?.file_url ?? part.image_url;
  if (!src) return null;
  // `loading="lazy"` keeps the drawer's first-paint snappy when many
  // parts get opened in succession via the bulk-refresh review flow.
  return (
    <div className="flex justify-center rounded-md border border-stone-200 dark:border-stone-800 bg-stone-50 dark:bg-stone-900 p-2">
      <a
        href={src}
        target="_blank"
        rel="noopener noreferrer"
        title="Open full-resolution image"
        className="block"
      >
        <img
          src={src}
          alt={part.mfg_pn ?? part.part_number}
          className="max-h-48 w-auto object-contain"
          loading="lazy"
          referrerPolicy={first ? undefined : 'no-referrer'}
        />
      </a>
    </div>
  );
}

function WhereUsedSection({
  orgSlug,
  partId,
}: {
  orgSlug: string;
  partId: number;
}) {
  const rows = useWhereUsed(orgSlug, partId);
  if (rows.isPending || !rows.data) return null;
  if (rows.data.length === 0) {
    return (
      <Section title="Where used">
        <p className="text-stone-500 dark:text-stone-400">Not in any BOM.</p>
      </Section>
    );
  }
  return (
    <Section title="Where used">
      <ul className="space-y-1">
        {rows.data.map((row) => (
          <li
            key={`${row.bom_id}-${row.revision_id}`}
            className="flex items-center justify-between border-b border-stone-100 dark:border-stone-800 py-1 last:border-0"
          >
            <Link
              to={`/boms/${row.bom_id}`}
              className="font-medium text-forge-700 dark:text-forge-400 hover:underline"
            >
              {row.bom_name}
            </Link>
            <span className="text-xs text-stone-500 dark:text-stone-400">
              Rev {row.revision_label}
              {row.revision_frozen ? ' (frozen)' : ' (draft)'} · {row.quantity_per_board}/board
            </span>
          </li>
        ))}
      </ul>
    </Section>
  );
}

function CategoryField({
  orgSlug,
  categoryId,
}: {
  orgSlug: string;
  categoryId: number | null;
}) {
  const cats = usePartCategories(orgSlug);
  if (categoryId === null) return <Field label="Category" value="—" />;
  const cat = cats.data?.find((c) => c.id === categoryId);
  if (!cat) return <Field label="Category" value={`#${categoryId}`} />;
  const parent = cat.parent_id
    ? cats.data?.find((c) => c.id === cat.parent_id)
    : null;
  const display = parent ? `${parent.name} › ${cat.name}` : cat.name;
  return <Field label="Category" value={display} />;
}

function JsonBlobSection({
  title,
  data,
}: {
  title: string;
  data: Record<string, unknown> | null | undefined;
}) {
  // Renders a JSON object as a key/value list. Each entry is shown
  // verbatim — these blobs are escape hatches for stuff PartForge
  // doesn't model (extra_fields, kicad_fields, external_refs), so the
  // most useful display is "what's in the bag" without trying to
  // interpret it. Skipped entirely when the blob is empty so it
  // doesn't add an empty section to the drawer.
  if (!data || Object.keys(data).length === 0) return null;
  return (
    <Section title={title}>
      {Object.entries(data).map(([k, v]) => (
        <Field
          key={k}
          label={k}
          value={typeof v === 'string' ? v : JSON.stringify(v)}
          mono
        />
      ))}
    </Section>
  );
}

function Section({
  title,
  children,
  action,
}: {
  title: string;
  children: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <section>
      <div className="mb-2 flex items-center justify-between">
        <h3 className="text-xs font-semibold uppercase tracking-wide text-stone-500 dark:text-stone-400">{title}</h3>
        {action}
      </div>
      <dl className="space-y-1">{children}</dl>
    </section>
  );
}

function Field({
  label,
  value,
  mono,
  link,
  partField,
}: {
  label: string;
  value: React.ReactNode;
  mono?: boolean;
  link?: boolean;
  /** When set, the field hides whenever the part's category doesn't
   * claim this Part attribute via the field-relevance rubric. Lets
   * the drawer drop irrelevant rows ("show empty fields" mode no
   * longer ends up with a TVS-only Vc clamp row on every resistor).
   * Common fields that aren't specialty-gated (mfg_pn, package, etc.)
   * pass `partField` only as a no-op identifier — the rubric's
   * `isPartFieldHidden` returns false for them. */
  partField?: string;
}) {
  const showEmpty = useContext(ShowEmptyFieldsContext);
  const categorySlug = useContext(CategorySlugContext);
  // Category gating is absolute in the drawer: irrelevant fields stay
  // hidden even in show-empty-fields mode. `showEmpty` only governs
  // whether *relevant-but-blank* rows render — that's the gap signal
  // Aaron's rubric calls for. We pass `showAll=false` (the function's
  // default) so the gate applies regardless of the toggle.
  if (partField && isPartFieldHidden(partField, categorySlug)) {
    return null;
  }
  const isEmpty = value === null || value === undefined || value === '';
  if (isEmpty && !showEmpty) return null;
  if (isEmpty) {
    return (
      <div className="grid grid-cols-3 gap-2">
        <dt className="text-stone-500 dark:text-stone-400">{label}</dt>
        <dd className="col-span-2 text-stone-300 dark:text-stone-600">—</dd>
      </div>
    );
  }
  return (
    <div className="grid grid-cols-3 gap-2">
      <dt className="text-stone-500 dark:text-stone-400">{label}</dt>
      <dd className={cn('col-span-2 text-stone-800 dark:text-stone-200', mono && 'font-mono')}>
        {link && typeof value === 'string' ? (
          <a className="text-forge-700 dark:text-forge-400 hover:underline" href={value} target="_blank" rel="noreferrer">
            {value}
          </a>
        ) : (
          value
        )}
      </dd>
    </div>
  );
}

function LabeledInline({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="grid grid-cols-3 items-center gap-2 text-sm">
      <span className="text-stone-500 dark:text-stone-400">{label}</span>
      <div className="col-span-2">{children}</div>
    </label>
  );
}

function formatBool(v: boolean | null | undefined): string | null {
  if (v === null || v === undefined) return null;
  return v ? 'Yes' : 'No';
}

function formatNumber(v: number | null, unit: string): string | null {
  if (v === null) return null;
  return `${v} ${unit}`;
}

function formatTempRange(min: number | null, max: number | null): string | null {
  if (min === null && max === null) return null;
  return `${min ?? '—'} to ${max ?? '—'} °C`;
}

function kicadStatus(part: PartDetail): string {
  const hasSymbol = Boolean(part.kicad_symbol?.trim());
  const hasFootprint = Boolean(part.kicad_footprint?.trim());
  if (hasSymbol && hasFootprint) {
    return part.kicad_has_3d_model
      ? '✓ Fully defined (symbol + footprint + 3D model)'
      : 'Linked (symbol + footprint, no 3D model)';
  }
  if (hasSymbol || hasFootprint) return 'Partial — only one of symbol/footprint set';
  if (part.kicad_tracked) return 'Marked tracked (legacy flag, no LIB_ID set)';
  return 'Not linked';
}
